import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PriceChart = ({ crypto }) => {
  const [timeframe, setTimeframe] = useState('24h');
  
  const timeframes = [
    { value: '1h', label: '1H' },
    { value: '24h', label: '24H' },
    { value: '7d', label: '7D' },
    { value: '30d', label: '30D' },
    { value: '1y', label: '1Y' }
  ];

  // Mock chart data
  const generateChartData = (period) => {
    const dataPoints = period === '1h' ? 60 : period === '24h' ? 24 : period === '7d' ? 7 : period === '30d' ? 30 : 365;
    const basePrice = crypto?.price;
    const data = [];
    
    for (let i = 0; i < dataPoints; i++) {
      const variation = (Math.random() - 0.5) * 0.1;
      const price = basePrice * (1 + variation);
      const time = period === '1h' ? `${i}:00` : 
                   period === '24h' ? `${i}:00` :
                   period === '7d' ? `Day ${i + 1}` :
                   period === '30d' ? `${i + 1}` :
                   `Month ${i + 1}`;
      
      data?.push({
        time,
        price: price,
        volume: Math.random() * 1000000
      });
    }
    
    return data;
  };

  const chartData = generateChartData(timeframe);
  const isPositive = crypto?.change >= 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-brand-sm p-3 shadow-brand">
          <p className="text-sm font-medium text-foreground">{label}</p>
          <p className="text-sm text-primary">
            Price: ${payload?.[0]?.value?.toFixed(6)}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-brand p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{crypto?.name} Price Chart</h3>
          <div className="flex items-center space-x-2 mt-1">
            <span className="text-2xl font-bold text-foreground">
              ${crypto?.price?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 6 })}
            </span>
            <div className={`flex items-center space-x-1 px-2 py-1 rounded-brand-xs text-sm font-medium ${
              isPositive 
                ? 'bg-success/10 text-success' :'bg-destructive/10 text-destructive'
            }`}>
              <Icon 
                name={isPositive ? "TrendingUp" : "TrendingDown"} 
                size={14} 
              />
              <span>{Math.abs(crypto?.change)?.toFixed(2)}%</span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-1 bg-muted rounded-brand-sm p-1">
          {timeframes?.map((tf) => (
            <Button
              key={tf?.value}
              variant={timeframe === tf?.value ? "default" : "ghost"}
              size="sm"
              onClick={() => setTimeframe(tf?.value)}
              className="h-8 px-3 text-xs"
            >
              {tf?.label}
            </Button>
          ))}
        </div>
      </div>
      <div className="h-80 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="time" 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              fontSize={12}
              tickFormatter={(value) => `$${value?.toFixed(2)}`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line 
              type="monotone" 
              dataKey="price" 
              stroke={isPositive ? "var(--color-success)" : "var(--color-destructive)"}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: isPositive ? "var(--color-success)" : "var(--color-destructive)" }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div className="bg-muted/50 rounded-brand-sm p-3">
          <p className="text-muted-foreground">24h High</p>
          <p className="font-semibold text-foreground">${crypto?.high24h?.toFixed(6)}</p>
        </div>
        <div className="bg-muted/50 rounded-brand-sm p-3">
          <p className="text-muted-foreground">24h Low</p>
          <p className="font-semibold text-foreground">${crypto?.low24h?.toFixed(6)}</p>
        </div>
        <div className="bg-muted/50 rounded-brand-sm p-3">
          <p className="text-muted-foreground">Volume</p>
          <p className="font-semibold text-foreground">${(crypto?.volume / 1e6)?.toFixed(2)}M</p>
        </div>
        <div className="bg-muted/50 rounded-brand-sm p-3">
          <p className="text-muted-foreground">Market Cap</p>
          <p className="font-semibold text-foreground">${(crypto?.marketCap / 1e9)?.toFixed(2)}B</p>
        </div>
      </div>
    </div>
  );
};

export default PriceChart;